/**
 * PHYSICAL-RISK.STORE.TS
 */
import { createEntityStore } from "@/core/factory/store"
import { createEmptyPhysicalRisk, PHYSICAL_RISK_ENTITY, PhysicalRisk } from "./physical-risk.types"
import { formatPhysicalRisk, formatPhysicalRisksForPopulation } from "./physical-risk.formatter"
import { AccountRisksSettings } from "@/redux/account.types"
import { createSelector } from "@reduxjs/toolkit"
import { addPopulation, edit, removePopulation, removeSite, updatePhoto, visit } from "./physical-risk.thunk"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import { intersection, orderBy } from "lodash"
import {
  createUndefinedRiskFamily,
  RiskFamily,
  RISK_FAMILY_UNDEFINED_ID
} from "@/entities/risk-family/model/risk-family.types"
import { Population } from "@/entities/population/model/population.types"

export type PhysicalRiskContext = {
  accountRisksSettings: AccountRisksSettings
  riskFamilies: RiskFamily[]
  populations: Population[]
}

function isRiskForFamily(risk: PhysicalRisk, familyId: string) {
  return familyId === RISK_FAMILY_UNDEFINED_ID ? !risk.RiskFamilyId : risk.RiskFamilyId === familyId
}

function getPopulationSizeForRisks(risks: PhysicalRisk[], populations: Population[]) {
  const populationIds = new Set(risks.flatMap((risk) => risk.populationIds))
  return populations
    .filter((population) => populationIds.has(population.id))
    .reduce((size, population) => size + (population.size || 0), 0)
}

const physicalRiskContextSelector = createSelector(
  [
    (state: any) => state._session.accountRisksSettings,
    riskFamilyStore.selectors.list,
    (state: any) => state.population.list
  ],
  (accountRisksSettings, riskFamilies, populations) => ({
    accountRisksSettings,
    riskFamilies,
    populations
  })
)

const createPhysicalRiskStore = () => {
  const base = createEntityStore<PhysicalRisk, PhysicalRiskContext>(
    PHYSICAL_RISK_ENTITY,
    createEmptyPhysicalRisk,
    physicalRiskContextSelector,
    formatPhysicalRisk
  )

  /**
   * Detect if the risk is linked with other items
   */
  const isShared = createSelector([base.selectors.draft], (draft) => {
    return draft.siteIds.length + draft.populationIds.length > 1
  })

  /**
   * Get the max risk for one familyy
   */
  const maxForFamily = createSelector(
    [base.selectors.list, (_: any, familyId: string) => familyId],
    (risks: PhysicalRisk[], familyId: string) => {
      return (
        orderBy(
          risks.filter((x) => isRiskForFamily(x, familyId)),
          ["cValueEffective"],
          ["desc"]
        )[0] ?? null
      )
    }
  )

  /**
   * List families sorted
   */
  const listFamilies = createSelector(
    [riskFamilyStore.selectors.list, base.selectors.list, (state: any) => state.population.list, base.selectors.sort],
    (families, risks, populations, sort) => {
      const risksByFamily = risks.reduce(
        (acc, risk) => {
          const familyId = risk.RiskFamilyId ?? RISK_FAMILY_UNDEFINED_ID

          acc[familyId] ??= []
          acc[familyId].push(risk)

          return acc
        },
        {} as Record<string, PhysicalRisk[]>
      )
      const statsByFamily = Object.fromEntries(
        Object.entries(risksByFamily).map(([familyId, familyRisks]) => [
          familyId,
          {
            size: getPopulationSizeForRisks(familyRisks, populations),
            maxRisk: Math.max(...familyRisks.map((risk) => risk.cValueEffective || 0))
          }
        ])
      )
      const displayFamilies = risks.some((risk) => !risk.RiskFamilyId)
        ? families.concat([createUndefinedRiskFamily()])
        : families

      return orderBy(
        displayFamilies,
        [
          (family) => {
            if (sort.key === "cName") return family.name
            if (sort.key === "cSize") return statsByFamily[family.id]?.size || 0
            return statsByFamily[family.id]?.maxRisk || 0
          }
        ],
        [sort.key === "cName" ? "asc" : "desc"]
      )
    }
  )

  /**
   * List of risks filtered by search and
   */
  const listFiltered = createSelector(
    [base.selectors.list, (state) => state.site.selected, (state) => state.population.selected],
    (risks, selectedSites, selectedPopulations) => {
      const noSite = !selectedSites.length
      const noPop = !selectedPopulations.length
      return risks.filter(
        (x) =>
          (noSite || intersection(x.siteIds, selectedSites).length > 0) &&
          (noPop || intersection(x.populationIds, selectedPopulations).length > 0)
      )
    }
  )

  /**
   * Get all risks for one family
   */
  const listFilteredForFamily = createSelector(
    [listFiltered, (state) => state.riskFamily.active],
    (risks, activeFamily) => {
      return risks.filter((x) => isRiskForFamily(x, activeFamily.id))
    }
  )

  /**
   * Get all risks for the active population.
   */
  const listForPopulation = createSelector(
    [base.selectors.list, (state) => state.population.active, physicalRiskContextSelector],
    (risks, activePopulation, context) => formatPhysicalRisksForPopulation(risks, context, activePopulation.id)
  )

  /**
   * Get risks for the active population, respecting the current risk and site filters.
   */
  const listFilteredForPopulation = createSelector(
    [listForPopulation, (state) => state.physicalRisk.selected, (state) => state.site.selected],
    (risks, selectedRisks: string[], selectedSites: string[]) => {
      return risks.filter(
        (risk) =>
          (!selectedRisks.length || selectedRisks.includes(risk.id)) &&
          (!selectedSites.length || intersection(risk.siteIds, selectedSites).length > 0)
      )
    }
  )

  /**
   * Get risks for active site
   * Concat risks of the site and population selected for the site
   */
  const listForSite = createSelector([base.selectors.list, (state) => state.site.active], (risks, activeSite) => {
    return risks.filter((x) => x.siteIds.includes(activeSite.id))
  })

  /**
   * Get the size of all population for the family
   */
  const sizeForFamily = createSelector(
    [base.selectors.list, (state: any) => state.population.list, (_: any, familyId: string) => familyId],
    (risks: PhysicalRisk[], populations: Population[], familyId: string) => {
      return getPopulationSizeForRisks(
        risks.filter((x) => isRiskForFamily(x, familyId)),
        populations
      )
    }
  )

  return {
    ...base,
    selectors: {
      ...base.selectors,
      context: physicalRiskContextSelector,
      isShared,
      maxForFamily,
      listFamilies,
      listFiltered,
      listFilteredForFamily,
      listForPopulation,
      listFilteredForPopulation,
      listForSite,
      sizeForFamily
    },
    thunks: {
      ...base.thunks,
      addPopulation,
      edit,
      visit,
      removePopulation,
      removeSite,
      updatePhoto
    }
  }
}

const physicalRiskStore = createPhysicalRiskStore()
export default physicalRiskStore
